import { CATALOGUE, toPixels, toSeed, type Selection, type RenderOptions } from './avatar';
import { GARMENT_PALETTES } from './avatar-items';

/** A full-body portrait, composed from independently animated pixel layers. */
export const CHARACTER_WIDTH = 32;
export const CHARACTER_HEIGHT = 40;
export type CharacterLayer = 'back' | 'legs' | 'body' | 'leftArm' | 'rightArm' | 'head' | 'prop';
type Cell = string | null;
export type CharacterLayers = Record<CharacterLayer, Cell[][]>;
const layerNames: CharacterLayer[] = ['back', 'legs', 'body', 'leftArm', 'rightArm', 'head', 'prop'];
export const partKey = (sel: Selection, rail: keyof Selection) => CATALOGUE.find((r) => r.id === rail)?.parts[sel[rail] ?? 0]?.key ?? 'none';

export function characterLayers(sel: Selection, options: RenderOptions = {}): CharacterLayers {
  const layers = Object.fromEntries(layerNames.map((name) => [name, Array.from({ length: CHARACTER_HEIGHT }, () => Array<Cell>(CHARACTER_WIDTH).fill(null))])) as CharacterLayers;
  const compact = toPixels(sel, options);
  const garment = GARMENT_PALETTES[sel.color ?? 0] ?? GARMENT_PALETTES[0]!;
  const skin = compact[6]?.[7] ?? '#dcdcdc';
  const outline = options.light ? '#242a34' : '#20242d';
  const ink = '#171c26';
  const metal = '#a8b5c4';
  const accent = options.light ? '#3f6b00' : '#a3f01c';
  const white = '#e9edf2';
  const paint = (layer: CharacterLayer, x: number, y: number, w: number, h: number, color: string) => {
    for (let yy = y; yy < y + h; yy++) for (let xx = x; xx < x + w; xx++) {
      if (xx >= 0 && xx < CHARACTER_WIDTH && yy >= 0 && yy < CHARACTER_HEIGHT) layers[layer][yy]![xx] = color;
    }
  };
  const box = (layer: CharacterLayer, x: number, y: number, w: number, h: number, fill: string) => {
    paint(layer, x, y, w, h, outline); paint(layer, x + 1, y + 1, w - 2, h - 2, fill);
  };
  // A small shadow sits behind boots; it never belongs to the person or prop.
  paint('back', 8, 39, 16, 1, options.light ? '#ced3d9' : '#161a22');
  const kit = partKey(sel, 'kit');
  const sleeves = !['tank', 'tee', 'apron', 'blouse'].includes(kit);
  const suit = kit === 'spacesuit';
  const coat = ['labcoat', 'raincoat', 'poncho'].includes(kit);
  const torso = suit || kit === 'labcoat' ? white : garment.G;
  const pants = suit ? metal : garment.g;
  const wide = sel.build === 2 ? 1 : sel.build === 1 ? -1 : 0;
  box('legs', 10 - wide, 27, 6 + wide, 10, pants);
  box('legs', 17, 27, 6 + wide, 10, pants);
  paint('legs', 12 - wide, 29, 1, 6, garment.J);
  paint('legs', 19, 29, 1, 6, garment.J);
  const boot = partKey(sel, 'boots');
  for (const x of [9 - wide, 17]) {
    const height = ['high tops', 'workboots', 'moonboots'].includes(boot) ? 5 : 3;
    box('legs', x, 39 - height, 7 + wide, height, boot === 'workboots' ? '#aa7d4e' : boot === 'moonboots' ? white : garment.J);
    paint('legs', x, 38, 7 + wide, 1, boot === 'runners' ? accent : white);
    if (boot === 'high tops') paint('legs', x + 2, 35, 3, 1, white);
  }
  // Neck, shoulders, torso and separately drawn arms.
  paint('body', 14, 14, 4, 4, skin);
  box('body', 10 - wide, 17, 12 + 2 * wide, coat ? 15 : 11, torso);
  paint('body', 12, 16, 8, 2, torso);
  for (const [layer, x] of [['leftArm', 5 - wide], ['rightArm', 23 + wide]] as const) {
    box(layer, x, 18, 5, 9, sleeves ? torso : skin);
    paint(layer, x + 1, 19, 1, 5, sleeves ? garment.J : skin);
    box(layer, x, 26, 5, 4, skin);
    if (sleeves) paint(layer, x, 25, 5, 2, garment.g);
  }
  paint('body', 10 - wide, 27, 12 + 2 * wide, 1, garment.g);
  if (kit === 'tee') {
    paint('body', 14, 17, 4, 1, skin); paint('body', 13, 18, 6, 1, garment.g);
    paint('body', 18, 21, 2, 2, garment.J);
  }
  if (kit === 'tank') {
    paint('body', 12, 17, 8, 3, skin); paint('body', 11, 17, 2, 4, garment.G); paint('body', 19, 17, 2, 4, garment.G);
    paint('body', 12, 20, 8, 1, garment.g);
  }
  if (kit === 'shirt') {
    paint('body', 12, 17, 2, 2, garment.J); paint('body', 18, 17, 2, 2, garment.J);
    paint('body', 13, 19, 2, 1, garment.J); paint('body', 17, 19, 2, 1, garment.J);
  }
  if (kit === 'jacket' || kit === 'vest') {
    paint('body', 14, 18, 4, 8, white);
    paint('body', 12, 18, 2, 3, garment.g); paint('body', 18, 18, 2, 3, garment.g);
    paint('body', 13, 21, 1, 3, garment.g); paint('body', 18, 21, 1, 3, garment.g);
    if (kit === 'vest') {
      paint('leftArm', 6 - wide, 19, 3, 6, white); paint('rightArm', 24 + wide, 19, 3, 6, white);
      paint('body', 16, 19, 1, 7, garment.g);
    }
  }
  if (kit === 'blouse') {
    paint('body', 13, 17, 6, 1, skin); paint('body', 14, 18, 4, 1, skin); paint('body', 15, 19, 2, 1, skin);
    paint('body', 10 - wide, 25, 12 + 2 * wide, 1, garment.g);
    for (const x of [12, 16, 20]) paint('body', x, 26, 1, 2, garment.J);
  }
  if (kit === 'wraptop') {
    for (let i = 0; i < 7; i++) paint('body', 12 + i, 18 + i, 2, 1, garment.J);
    paint('body', 10 - wide, 26, 12 + 2 * wide, 1, garment.g); paint('body', 20, 26, 3, 2, garment.J);
  }
  if (['hoodie', 'bomber', 'raincoat'].includes(kit)) {
    paint('body', 12, 17, 2, 2, garment.g); paint('body', 18, 17, 2, 2, garment.g);
    paint('body', 13, 19, 1, 4, white); paint('body', 18, 19, 1, 4, white);
    paint('body', 13, 24, 6, 2, garment.g);
  }
  if (['jacket', 'shirt', 'bomber', 'raincoat', 'utility', 'varsity'].includes(kit)) {
    paint('body', 15, 18, 1, 9, ink);
    for (const y of [20, 23, 26]) paint('body', 16, y, 1, 1, metal);
  }
  if (kit === 'varsity') {
    paint('leftArm', 6 - wide, 19, 3, 6, white); paint('rightArm', 24 + wide, 19, 3, 6, white);
    paint('body', 18, 20, 2, 3, white); paint('body', 19, 21, 2, 1, torso);
  }
  if (kit === 'labcoat') {
    paint('body', 14, 18, 4, 13, garment.G);
    paint('body', 12, 18, 2, 3, metal); paint('body', 18, 18, 2, 3, metal);
    paint('body', 11, 25, 3, 3, metal); paint('body', 19, 25, 3, 3, metal);
  }
  if (['apron', 'overalls'].includes(kit)) {
    paint('body', 12, 17, 2, 10, garment.g); paint('body', 18, 17, 2, 10, garment.g);
    box('body', 12, 21, 8, 8, garment.g); paint('body', 14, 24, 4, 2, garment.J);
  }
  if (kit === 'hivis') {
    paint('body', 12, 18, 2, 9, '#dcc958'); paint('body', 18, 18, 2, 9, '#dcc958');
    paint('body', 10, 24, 12, 2, white);
  }
  if (kit === 'flannel') {
    for (const y of [20, 24]) paint('body', 11 - wide, y, 10 + 2 * wide, 1, garment.g);
    for (const x of [13, 18]) paint('body', x, 18, 1, 9, garment.g);
  }
  if (kit === 'poncho') {
    box('body', 7, 20, 18, 9, garment.G); paint('body', 8, 26, 16, 2, garment.J);
  }
  if (kit === 'scarf' || kit === 'turtleneck') {
    paint('body', 13, 15, 6, 3, garment.g);
    if (kit === 'scarf') paint('body', 19, 17, 2, 7, garment.J);
  }
  if (kit === 'spacesuit' || kit === 'utility') {
    box('body', 12, 20, 8, 5, suit ? metal : garment.g);
    paint('body', 14, 22, 2, 1, accent); paint('body', 17, 22, 1, 1, white);
    box('back', 7, 17, 18, 13, garment.g);
  }
  if (kit === 'lanyard') { paint('body', 13, 18, 1, 5, metal); paint('body', 18, 18, 1, 5, metal); box('body', 14, 22, 4, 4, white); }
  // The original face is still the face: no identity redesign or reroll.
  for (let y = 0; y < 11; y++) for (let x = 0; x < 16; x++) layers.head[y + 4]![x + 8] = compact[y]![x] ?? null;
  const hair = partKey(sel, 'hair');
  if (['long', 'locs', 'hijab', 'bob', 'braids'].includes(hair)) {
    const hairInk = compact[4]![3] ?? garment.g;
    paint('head', 9, 12, 2, hair === 'bob' ? 4 : 9, hairInk);
    paint('head', 21, 12, 2, hair === 'bob' ? 4 : 9, hairInk);
  }
  const prop = partKey(sel, 'prop');
  // Props are painted over the hand, so held objects never float beside it.
  if (prop === 'mug') {
    box('prop', 23, 25, 6, 5, white); paint('prop', 24, 25, 4, 1, '#76533e');
    box('prop', 28, 26, 3, 3, white); paint('prop', 26, 27, 1, 1, accent);
  } else if (prop === 'laptop') {
    box('prop', 10, 22, 15, 8, metal); paint('prop', 12, 24, 11, 4, '#26384e');
    paint('prop', 14, 25, 4, 1, accent); paint('prop', 14, 27, 7, 1, white);
    paint('prop', 8, 30, 19, 2, metal); paint('prop', 12, 30, 9, 1, ink);
  } else if (prop === 'notebook' || prop === 'book') {
    box('prop', 23, 23, 7, 9, prop === 'book' ? '#ae6657' : '#d4b279');
    paint('prop', 24, 24, 1, 7, white); paint('prop', 26, 25, 2, 1, ink); paint('prop', 26, 27, 2, 1, ink);
    paint('prop', 24, 29, 3, 1, skin);
  } else if (prop === 'wrench') {
    paint('prop', 26, 20, 2, 12, metal); paint('prop', 24, 19, 2, 4, metal); paint('prop', 28, 19, 2, 4, metal);
    paint('prop', 24, 23, 6, 2, metal); paint('prop', 25, 28, 4, 2, skin);
  } else if (prop === 'plant') {
    box('prop', 23, 27, 7, 5, '#bd7d58'); paint('prop', 26, 21, 1, 6, '#54795c');
    paint('prop', 23, 21, 3, 2, '#81b178'); paint('prop', 27, 23, 3, 2, '#81b178'); paint('prop', 26, 19, 2, 3, '#a1c789');
  } else if (prop === 'camera') {
    box('prop', 21, 25, 10, 6, garment.g); box('prop', 24, 26, 4, 4, '#a0bed8');
    paint('prop', 23, 24, 3, 1, metal); paint('prop', 29, 26, 1, 1, white);
  } else if (prop === 'robot') {
    box('prop', 24, 20, 7, 7, metal); paint('prop', 25, 22, 1, 1, accent); paint('prop', 29, 22, 1, 1, accent);
    paint('prop', 27, 18, 1, 2, metal); paint('prop', 27, 17, 1, 1, accent);
    box('prop', 24, 27, 7, 6, garment.G); paint('prop', 25, 33, 2, 2, metal); paint('prop', 29, 33, 2, 2, metal);
  } else if (prop === 'tablet') {
    box('prop', 21, 23, 9, 9, ink); paint('prop', 23, 25, 5, 5, '#819abc'); paint('prop', 24, 26, 3, 2, garment.J);
    paint('prop', 20, 24, 1, 6, white);
  }
  return layers;
}

/** Merge adjacent pixels into runs; profile SVGs stay small and easy to animate. */
function runs(grid: Cell[][]): string {
  return grid.map((row, y) => {
    const rects: string[] = [];
    for (let x = 0; x < row.length;) {
      const c = row[x]; let end = x + 1;
      while (end < row.length && row[end] === c) end++;
      if (c) rects.push(`<rect x="${x}" y="${y}" width="${end - x}" height="1" fill="${c}"/>`);
      x = end;
    }
    return rects.join('');
  }).join('');
}
const cache = new Map<string, string>();
export function characterSVG(sel: Selection, opts: RenderOptions = {}): string {
  const scale = Math.max(1, Math.round(opts.scale ?? 1));
  const key = `${toSeed(sel)}|${opts.light ? 1 : 0}|${scale}`;
  const found = cache.get(key); if (found) return found;
  const layers = characterLayers(sel, opts);
  const blink = ['scan', 'dot', 'dim', 'wide', 'wink'].includes(partKey(sel, 'eyes')) ? `<g class="character-blink" style="opacity:0"><rect x="13" y="8" width="2" height="2" fill="${toPixels(sel, opts)[6]![7] ?? '#dcdcdc'}"/><rect x="18" y="8" width="2" height="2" fill="${toPixels(sel, opts)[6]![7] ?? '#dcdcdc'}"/><path d="M13 9h2M18 9h2" stroke="#171c26" stroke-width="1"/></g>` : '';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" class="character-art" width="${CHARACTER_WIDTH * scale}" height="${CHARACTER_HEIGHT * scale}" viewBox="0 0 32 40" shape-rendering="crispEdges" aria-hidden="true">${layerNames.map((name) => `<g class="character-${name}">${runs(layers[name])}${name === 'head' ? blink : ''}</g>`).join('')}</svg>`;
  if (cache.size >= 200) cache.clear(); cache.set(key, svg);
  return svg;
}
